/* eslint-disable max-lines */
import type { GraphQLAuditItem, GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';
import { Text, LoadingSpinner, MoreActions, Icon, IconSize, SlideSheet } from '@soteria/design-system-ve';
import { useCallback, useEffect, useMemo } from 'react';

import { DeleteDialog } from '../../../core/components/DeleteDialog';
import { MessageToast } from '../../../core/components/MessageToast';
import { useAuditSessionFilterOptions } from '../../hooks/useAuditSessionFilterOptions';

import * as styles from './AuditDecisionPanel.css';
import { ActionMenu } from './components/ActionMenu';
import { CommentsSection } from './components/CommentsSection';
import { ErrorToasts } from './components/ErrorToasts';
import { LabelsSection } from './components/LabelsSection';
import { LanguageDetectionSection } from './components/LanguageDetectionSection';
import { OcrSection } from './components/OcrSection';
import { PolicyAssessmentSection } from './components/PolicyAssessmentSection';
import { TranscriptionSection } from './components/TranscriptionSection';
import { useAuditDecisionPanel } from './hooks/useAuditDecisionPanel';
import * as spacingStyles from './spacing.css';

interface AuditDecisionPanelProps {
  selectedItem: GraphQLAuditItem | null;
  currentUserDecision?: GraphQLAuditDecision;
  onSave: (decision: GraphQLAuditDecision) => void;
  onClose: () => void;
  /** Whether the audit session control bar is visible (affects positioning) */
  isControlBarVisible?: boolean;
  /** Community ID for fetching available standard labels */
  communityId: string;
  /** Callback to report loading state changes */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Whether the audit session is completed (makes panel read-only) */
  isSessionCompleted?: boolean;
}

// eslint-disable-next-line complexity
export function AuditDecisionPanel({
  selectedItem,
  currentUserDecision,
  onSave,
  onClose,
  isControlBarVisible = false,
  communityId,
  onLoadingChange,
  isSessionCompleted = false,
}: AuditDecisionPanelProps) {
  // Get available standard labels for the multi-select
  const { filterOptions, loading: filterOptionsLoading } = useAuditSessionFilterOptions(communityId);

  // Consolidated hook that provides all functionality with reduced cognitive load
  const {
    // Form data
    formData,
    setFormData,

    // Helper functions
    getClassificationResults,
    getTranscription,
    getOcrText,
    getDetectedLanguage,

    // Actions
    loading,
    handleSave: saveAction,
    handleMarkAllCorrect: markAllCorrectAction,

    // Delete functionality
    isDeleteDialogOpen,
    deleteLoading,
    deleteErrors,
    deleteErrorToast,
    handleDeleteDecision,
    openDeleteDialog,
    closeDeleteDialog,
    clearErrorToast,

    // Skip functionality
    handleSkip,
    skipLoading,
    skipErrors,

    // Panel state
    isActionMenuOpen,
    setIsActionMenuOpen,
    actionMenuButtonId,
    showValidationErrors,
    setShowValidationErrors,
    validationToast,
    setValidationToast,
    handleActionMenuClose,
  } = useAuditDecisionPanel({
    selectedItem,
    currentUserDecision,
    onSave,
    onClose,
    isSessionCompleted,
    getClassificationResults: item => {
      if (!item?.contentSnapshot?.classificationResults) return [];

      return item.contentSnapshot.classificationResults.map(result => ({ name: result.name, severity: result.severity }));
    },
  });

  // Report loading state changes to parent
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  // Create multi-select options from available standard labels and severity levels
  const labelOptions = useMemo(() => {
    if (!filterOptions?.standardLabels || !filterOptions?.severityLevels) {
      return [];
    }

    const options: Array<{ id: string; value: string; label: string }> = [];

    // Create options for each label-severity combination
    filterOptions.standardLabels.forEach(standardLabel => {
      filterOptions.severityLevels.forEach(severity => {
        const key = `${standardLabel.labelName}-${severity}`;

        options.push({
          id: key,
          value: key,
          label: `${standardLabel.labelName.toUpperCase()} ${severity}`,
        });
      });
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [filterOptions]);

  // Wrapper functions to pass form data with validation
  // eslint-disable-next-line complexity
  const handleSave = useCallback(async () => {
    const isAudioContent = selectedItem?.contentSnapshot?.contentType?.toLowerCase() === 'audio';
    const isImageContent = selectedItem?.contentSnapshot?.contentType?.toLowerCase() === 'image';
    const missingFields = [];

    // Check required fields for all content types
    if (formData.labelsCorrect === null) missingFields.push('Label Assessment');
    if (formData.policiesCorrect === null) missingFields.push('Policy Assessment');

    // Only require language and transcription assessment for audio content
    if (isAudioContent) {
      if (formData.languageCorrect === null) missingFields.push('Language Assessment');
      if (formData.transcriptionCorrect === null) missingFields.push('Transcription Assessment');
    }

    // Only require OCR assessment for image content
    if (isImageContent) {
      if (formData.ocrCorrect === null) missingFields.push('OCR Assessment');
    }

    if (missingFields.length > 0) {
      setValidationToast(`Please provide ${missingFields.join(' and ')} before saving your decision.`);
      setShowValidationErrors(true);

      return;
    }

    // Clear validation state and proceed with save
    setShowValidationErrors(false);
    setValidationToast(null);
    await saveAction(formData);
  }, [saveAction, formData, setShowValidationErrors, setValidationToast, selectedItem]);

  const handleMarkAllCorrect = useCallback(async () => {
    await markAllCorrectAction(setFormData);
  }, [markAllCorrectAction, setFormData]);

  // Calculate top offset based on control bar visibility
  // The control bar has a height of approximately 64px when visible
  const topOffset = isControlBarVisible ? 64 : 0;

  // Action menu should always be available unless session is completed
  const showActionMenu = !isSessionCompleted;

  // Handle delete action from menu
  const handleDeleteFromMenu = useCallback(() => {
    if (currentUserDecision) {
      openDeleteDialog();
    }
  }, [openDeleteDialog, currentUserDecision]);

  // Handle skip action from menu
  const handleSkipFromMenu = useCallback(() => {
    handleSkip();
  }, [handleSkip]);

  return (
    <>
      <SlideSheet
        cancelAction={{
          label: 'Cancel',
        }}
        isOpen={!!selectedItem}
        primaryAction={{
          label: loading ? <LoadingSpinner /> : 'Save Decision',
          onClick: handleSave,
          disabled: loading || isSessionCompleted,
        }}
        secondaryAction={{
          label: 'Mark All Correct',
          onClick: handleMarkAllCorrect,
          disabled: isSessionCompleted,
          variant: 'secondary' as const,
        }}
        tertiaryAction={
          showActionMenu
            ? {
                label: <Icon icon={MoreActions} iconSize={IconSize.Small} />,
                onClick: () => setIsActionMenuOpen(prev => !prev),
                disabled: loading || deleteLoading || skipLoading,
                variant: 'secondary' as const,
                id: actionMenuButtonId,
              }
            : undefined
        }
        title={isSessionCompleted ? 'View Decision (Read Only)' : currentUserDecision ? 'Edit Your Decision' : 'Add Your Decision'}
        topOffset={topOffset}
        width="lg"
        onClose={onClose}
      >
        {!selectedItem ? (
          <div className={styles.emptyState}>
            <Text>Select an audit item to add or edit your decision</Text>
          </div>
        ) : (
          <div className={spacingStyles.flexColumnGap}>
            {/* Read-only message for completed sessions */}
            {isSessionCompleted && (
              <div className={spacingStyles.compactSection}>
                <Text className={styles.sectionTitle}>This audit session has been completed. Decisions are now read-only.</Text>
              </div>
            )}

            {/* Labels Section - Primary assessment for external QA */}
            <LabelsSection
              className={showValidationErrors && formData.labelsCorrect === null ? spacingStyles.requiredSection : spacingStyles.requiredSectionComplete}
              correctedLabels={formData.correctedLabels}
              filterOptionsLoading={filterOptionsLoading}
              getClassificationResults={getClassificationResults}
              isSessionCompleted={isSessionCompleted}
              labelOptions={labelOptions}
              labelsCorrect={formData.labelsCorrect}
              selectedItem={selectedItem}
              onCorrectedLabelsChange={selectedValues =>
                setFormData(prev => ({
                  ...prev,
                  correctedLabels: selectedValues,
                }))
              }
              onLabelsCorrectChange={value =>
                setFormData(prev => ({
                  ...prev,
                  labelsCorrect: value,
                }))
              }
            />

            {/* Policy Assessment Section - Primary assessment for external QA */}
            <PolicyAssessmentSection
              className={showValidationErrors && formData.policiesCorrect === null ? spacingStyles.requiredSection : spacingStyles.requiredSectionComplete}
              isSessionCompleted={isSessionCompleted}
              nonTriggeredPolicyAssessments={formData.nonTriggeredPolicyAssessments}
              policiesCorrect={formData.policiesCorrect}
              selectedItem={selectedItem}
              triggeredPolicyAssessments={formData.triggeredPolicyAssessments}
              onNonTriggeredPolicyAssessmentChange={newAssessment => {
                setFormData(prev => ({
                  ...prev,
                  nonTriggeredPolicyAssessments: prev.nonTriggeredPolicyAssessments.some(a => a.policyId === newAssessment.policyId)
                    ? prev.nonTriggeredPolicyAssessments.map(a => (a.policyId === newAssessment.policyId ? newAssessment : a))
                    : [...prev.nonTriggeredPolicyAssessments, newAssessment],
                }));
              }}
              onPoliciesCorrectChange={value =>
                setFormData(prev => ({
                  ...prev,
                  policiesCorrect: value,
                }))
              }
              onTriggeredPolicyAssessmentChange={newAssessment => {
                setFormData(prev => ({
                  ...prev,
                  triggeredPolicyAssessments: prev.triggeredPolicyAssessments.some(a => a.policyId === newAssessment.policyId)
                    ? prev.triggeredPolicyAssessments.map(a => (a.policyId === newAssessment.policyId ? newAssessment : a))
                    : [...prev.triggeredPolicyAssessments, newAssessment],
                }));
              }}
            />

            {/* Transcription Section - Only for audio content */}
            {selectedItem?.contentSnapshot?.contentType?.toLowerCase() === 'audio' && (
              <TranscriptionSection
                correctedTranscription={formData.correctedTranscription}
                getTranscription={getTranscription}
                isSessionCompleted={isSessionCompleted}
                selectedItem={selectedItem}
                transcriptionCorrect={formData.transcriptionCorrect}
                onCorrectedTranscriptionChange={value => setFormData(prev => ({ ...prev, correctedTranscription: value }))}
                onTranscriptionCorrectChange={value => setFormData(prev => ({ ...prev, transcriptionCorrect: value }))}
              />
            )}

            {/* OCR Section - Only for image content */}
            {selectedItem?.contentSnapshot?.contentType?.toLowerCase() === 'image' && (
              <OcrSection
                correctedOcr={formData.correctedOcr}
                getOcrText={getOcrText}
                isSessionCompleted={isSessionCompleted}
                ocrCorrect={formData.ocrCorrect}
                selectedItem={selectedItem}
                onCorrectedOcrChange={value => setFormData(prev => ({ ...prev, correctedOcr: value }))}
                onOcrCorrectChange={value => setFormData(prev => ({ ...prev, ocrCorrect: value }))}
              />
            )}

            {/* Language Detection Section - Only for audio content */}
            {selectedItem?.contentSnapshot?.contentType?.toLowerCase() === 'audio' && (
              <LanguageDetectionSection
                getDetectedLanguage={getDetectedLanguage}
                isSessionCompleted={isSessionCompleted}
                languageCorrect={formData.languageCorrect}
                selectedItem={selectedItem}
                onLanguageCorrectChange={value => setFormData(prev => ({ ...prev, languageCorrect: value }))}
              />
            )}

            {/* Comments Section */}
            <CommentsSection
              comments={formData.comments}
              isSessionCompleted={isSessionCompleted}
              onCommentsChange={(value: string) => setFormData(prev => ({ ...prev, comments: value }))}
            />
          </div>
        )}
      </SlideSheet>

      {/* Action Menu */}
      {showActionMenu && (
        <ActionMenu
          currentUserDecision={currentUserDecision}
          isOpen={isActionMenuOpen}
          triggerId={actionMenuButtonId}
          onClose={handleActionMenuClose}
          onDelete={handleDeleteFromMenu}
          onSkip={handleSkipFromMenu}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        description="Are you sure you want to delete this decision?"
        errors={deleteErrors}
        isDeleting={deleteLoading}
        isOpen={isDeleteDialogOpen}
        itemName="decision"
        itemType=""
        title="Delete decision"
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteDecision}
      />

      {/* Error Toasts */}
      <ErrorToasts deleteErrorToast={deleteErrorToast} skipErrors={skipErrors} onClearDeleteError={clearErrorToast} />

      {/* Validation Toast */}
      {validationToast && <MessageToast isVisible={!!validationToast} message={validationToast} variant="warning" onClose={() => setValidationToast(null)} />}
    </>
  );
}
